/**
 * timesheetUtils.js — pure helpers for clock records (`shifts`) and hour-change
 * requests (`shiftRequests`): spotting duplicates / mis-taps, overlap checks, and
 * break-aware minute counts. No Firebase here; unit-tested in timesheetUtils.test.js.
 *
 * Deleted clock records are kept with status 'deleted' (soft delete) so payroll keeps
 * an audit trail and Undo/Restore work; everything that counts hours skips them.
 */

/** Firestore Timestamp | Date | ISO string | millis → Date (or null). */
export function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const isDeleted = s => s.status === 'deleted';
const isOpen = s => s.status === 'active' || s.status === 'on-break';

/** A record's [start, end] — an open shift runs until `now`. */
function span(s, now) {
  const start = toDate(s.clockIn);
  const end = toDate(s.clockOut) || (isOpen(s) ? now : start);
  return start && end ? [start, end] : null;
}

const MISTAP_MIN = 2;   // shorter than this = almost certainly a mis-tap / test
const OVERLAP_MIN = 1;  // overlaps of a minute or less are rounding, not duplicates

/**
 * Records that look wrong, keyed by id → { kind, withId? }:
 *  'zero'    — a finished record under 2 minutes
 *  'overlap' — same person, times overlapping another record (double clock-in, or a
 *              manual add on top of a real punch). Deleted records are ignored.
 */
export function findTimesheetIssues(shifts, now = new Date()) {
  const issues = new Map();
  const live = shifts.filter(s => !isDeleted(s));
  live.forEach(s => {
    const sp = span(s, now);
    if (!sp) return;
    if (!isOpen(s) && (sp[1] - sp[0]) / 60000 < MISTAP_MIN) issues.set(s.id, { kind: 'zero' });
  });
  const byUser = {};
  live.forEach(s => { (byUser[s.userId] = byUser[s.userId] || []).push(s); });
  Object.values(byUser).forEach(list => {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = span(list[i], now), b = span(list[j], now);
        if (!a || !b) continue;
        const overlapMin = (Math.min(a[1], b[1]) - Math.max(a[0], b[0])) / 60000;
        const sameMoment = +a[0] === +b[0];
        if (overlapMin > OVERLAP_MIN || sameMoment) {
          if (!issues.has(list[i].id)) issues.set(list[i].id, { kind: 'overlap', withId: list[j].id });
          if (!issues.has(list[j].id)) issues.set(list[j].id, { kind: 'overlap', withId: list[i].id });
        }
      }
    }
  });
  return issues;
}

/** Live records of `userId` touching [start, end] (zero-length ones inside it count). */
export function overlappingRecords(shifts, { userId, start, end, ignoreId }, now = new Date()) {
  return shifts.filter(s => {
    if (isDeleted(s) || s.userId !== userId || s.id === ignoreId) return false;
    const sp = span(s, now);
    return sp && sp[0] <= end && sp[1] >= start;
  });
}

/** Minutes of break time that fall inside [start, end] (an open break runs to `now`). */
export function breakMinutesWithin(breaks = [], start, end, now = new Date()) {
  let ms = 0;
  breaks.forEach(b => {
    const bs = toDate(b.start);
    const be = toDate(b.end) || now;
    if (!bs) return;
    const from = Math.max(+bs, +start), to = Math.min(+be, +end);
    if (to > from) ms += to - from;
  });
  return Math.round(ms / 60000);
}

/** Paid minutes for a clock-in/out pair, minus the breaks taken inside it. */
export function workedMinutes(start, end, breaks = []) {
  return Math.max(0, Math.round((end - start) / 60000) - breakMinutesWithin(breaks, start, end, end));
}

/**
 * Pending requests that repeat another one → Map(duplicateId → keptId). The newest
 * request in a group is kept (it's the person's latest intent). Groups:
 *  edits / deletions — same type and same shift
 *  added shifts      — same person with overlapping requested times
 */
export function duplicatePendingRequests(requests) {
  const pending = requests.filter(r => r.status === 'pending');
  const created = r => +(toDate(r.createdAt) || 0);
  const newestFirst = [...pending].sort((a, b) => created(b) - created(a));
  const kept = [];
  const dupes = new Map();
  newestFirst.forEach(r => {
    const twin = kept.find(k => {
      if ((k.type || 'edit') !== (r.type || 'edit')) return false;
      if (r.type === 'new-shift') {
        if (k.userId !== r.userId) return false;
        const [a0, a1] = [toDate(r.requestedClockIn), toDate(r.requestedClockOut)];
        const [b0, b1] = [toDate(k.requestedClockIn), toDate(k.requestedClockOut)];
        return a0 && a1 && b0 && b1 && a0 < b1 && b0 < a1;
      }
      return !!r.shiftId && k.shiftId === r.shiftId;
    });
    if (twin) dupes.set(r.id, twin.id); else kept.push(r);
  });
  return dupes;
}
